import { TypographyWithEllipsis } from '@modules/common/components/TypographyWithEllipsis';
import { ArrowDropUp } from '@mui/icons-material';
import {
  alpha,
  Box,
  Button,
  ButtonProps,
  ClickAwayListener,
  Paper,
  Popper,
  PopperProps,
  useControlled,
} from '@mui/material';
import { useTheme } from '@mui/system';
import { KeyboardEvent, ReactNode, useEffect, useRef, useState } from 'react';

interface SelectButtonProps {
  className?: string;
  label: string;
  placeholder?: string;
  renderDropDownComponent: (props: {
    open: boolean;
    anchorEl: HTMLElement | null;
    onClose: () => void;
  }) => ReactNode;
  componentsProps?: {
    popper?: Partial<PopperProps>;
    button?: Partial<ButtonProps>;
  };
  startIcon?: ReactNode;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  shouldShowTooltip?: boolean;
  buttonColor: ButtonProps['color'];
  readOnly?: boolean;
}

export function SelectButton({
  className,
  isOpen,
  onOpen,
  onClose,
  label,
  placeholder = '',
  renderDropDownComponent,
  componentsProps = {},
  startIcon,
  shouldShowTooltip,
  buttonColor,
  readOnly = true,
}: SelectButtonProps) {
  const theme = useTheme();

  const { popper: popperProps = {}, button: buttonProps = {} } =
    componentsProps;
  const [isPopperOpen, setIsPopperOpen] = useControlled({
    controlled: isOpen,
    default: false,
    name: 'SelectButton',
    state: 'isPopperOpen',
  });

  const anchorRef = useRef<HTMLButtonElement>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Required to forceRender on anchor element present
  useEffect(() => {
    if (anchorRef.current) {
      setAnchorEl(anchorRef.current);
    }
  }, []);

  const openMenu = () => {
    if (!isPopperOpen && !readOnly) {
      setIsPopperOpen(true);
      onOpen?.();
    }
  };

  const closeMenu = () => {
    if (isPopperOpen) {
      setIsPopperOpen(false);
      onClose?.();
      anchorEl?.focus();
    }
  };

  const onClickAway = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    closeMenu();
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      closeMenu();
    }

    if (event.key === 'ArrowDown') {
      openMenu();
    }
  };

  return (
    <ClickAwayListener onClickAway={onClickAway}>
      <Box className={className} onKeyDown={onKeyDown}>
        <Button
          {...buttonProps}
          ref={anchorRef}
          size="small"
          variant="outlined"
          color={buttonColor}
          sx={{
            maxWidth: 400,
            ...buttonProps.sx,
            ...(isPopperOpen
              ? {
                  backgroundColor: alpha(
                    buttonColor && theme.palette[buttonColor].main
                      ? theme.palette[buttonColor].main
                      : theme.palette.primary.main,
                    0.08,
                  ),
                  borderColor:
                    buttonColor && theme.palette[buttonColor].main
                      ? theme.palette[buttonColor].main
                      : theme.palette.primary.main,
                }
              : {}),
            '&:hover': {
              cursor: readOnly ? 'default' : 'pointer',
            },
          }}
          onClick={openMenu}
          endIcon={
            !readOnly ? (
              <ArrowDropUp
                sx={{
                  transform: isPopperOpen ? undefined : 'rotate(180deg)',
                  color: (theme) =>
                    buttonProps.disabled
                      ? theme.palette.action.disabled
                      : alpha(theme.palette.action.active, 0.56),
                }}
              />
            ) : null
          }
          startIcon={startIcon}
        >
          <TypographyWithEllipsis
            variant="caption"
            textTransform="initial"
            sx={{
              color: 'text.primary',
            }}
            shouldShowTooltip={shouldShowTooltip}
            className="qb-select-button-label"
          >
            {label || placeholder}
          </TypographyWithEllipsis>
        </Button>
        <Popper
          open={anchorEl ? isPopperOpen : false}
          anchorEl={anchorEl}
          placement="bottom-start"
          {...popperProps}
          sx={{ ...popperProps.sx, zIndex: (theme) => theme.zIndex.modal }}
        >
          <Paper
            elevation={8}
            sx={{
              mt: 1,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            }}
          >
            {renderDropDownComponent({
              anchorEl,
              open: isPopperOpen,
              onClose: closeMenu,
            })}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}
